<template>
  <label
    :class="[
      $style.selector,
      {
        [$style['selector--disabled']]: disabled,
        [$style['selector--small']]: small,
        [$style['selector--round']]: round,
        [$style['selector--invalid']]: invalid,
      },
    ]"
  >
    <span
      :class="[
        $style.selector__text,
        $style.text,
        {
          [$style['selector__text--placeholder']]: isEmpty,
          [$style['selector__text--ellipsis']]: ellipsis,
          [$style['selector__text--small']]: small,
          [$style['selector__text--invalid']]: invalid,
        },
      ]"
    >
      <slot :value="modelValue" />
      {{ text }}
      <IconChevron
        :class="[
          $style.selector__icon,
          $style.icon,
          { [$style['icon--invalid']]: invalid },
        ]"
      />
    </span>
    <select
      :class="[
        $style.selector__entity,
        $style.select,
        {
          [$style['select--small']]: small,
        },
      ]"
      @change="onChange"
      v-model="value"
      :disabled="disabledValue"
    >
      <option
        :value="option.value"
        :key="index"
        v-for="(option, index) in options"
      >
        {{ option.text }}
      </option>
    </select>
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import type { Option, OptionValueType } from '@/types/app'

const props = withDefaults(
  defineProps<{
    modelValue?: OptionValueType
    options?: Option<OptionValueType>[]
    placeholder?: string
    disabled?: boolean
    ellipsis?: boolean
    small?: boolean
    round?: boolean
    invalid?: boolean
    onChange?: () => void
  }>(),
  {
    modelValue: null,
    options: () => [],
    placeholder: '',
    disabled: false,
    ellipsis: false,
    small: false,
    round: false,
    invalid: false,
    onChange: undefined,
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: OptionValueType]
}>()

const isEmpty = computed((): boolean => props.modelValue == null)

const text = computed((): string => {
  if (isEmpty.value) {
    return props.placeholder
  }
  const modelValueOption = props.options.find(
    (option: Option<OptionValueType>): boolean =>
      option.value === props.modelValue
  )
  return modelValueOption ? modelValueOption.text : '--'
})

const value = computed({
  get(): OptionValueType {
    return props.modelValue ?? null
  },
  set(value: OptionValueType): void {
    emit('update:modelValue', value)
  },
})

const disabledValue = computed(
  (): true | undefined => props.disabled || undefined
)
</script>

<style lang="scss" module>
.selector {
  $arrow-size: 24px;

  position: relative;
  display: inline-flex;
  height: 48px;
  box-sizing: border-box;
  align-items: center;
  padding-right: (16px + $arrow-size);
  padding-left: 16px;
  border: 1px solid $color-border-low-emphasis;
  background-color: $color-surface-primary;
  border-radius: 4px;
  outline: 0;
  transition:
    border-color $transition-base,
    color $transition-base,
    -webkit-text-fill-color $transition-base;

  &--invalid {
    border-color: $color-caution-border;
    background-color: $color-caution-surface;
  }

  &__icon {
    position: absolute;
    top: 0;
    right: 8px;
    bottom: 0;
    width: $arrow-size;
    height: $arrow-size;
    margin: auto;
    color: $color-text-and-object-low-emphasis;
    font-size: $arrow-size;
  }

  &__text {
    padding-bottom: 1px;
    color: $color-text-and-object-high-emphasis;
    font-size: 15px;

    &--placeholder {
      color: $color-text-and-object-low-emphasis;
    }

    &--ellipsis {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &--small {
      font-size: 12px;
    }

    &--invalid {
      color: $color-caution-accent;
    }
  }

  &__entity {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &::placeholder {
    color: $color-text-and-object-low-emphasis;
  }

  &:focus {
    border-color: $color-border-medium-emphasis;
  }

  &--disabled {
    background-color: $color-surface-secondary;
  }

  &--small {
    height: 28px;
  }

  &--round {
    border-radius: 20px;
  }
}

.text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select {
  box-sizing: border-box;
  appearance: none;
  font-size: 14px;
  opacity: 0;

  &--small {
    font-size: 12px;
  }
}

.icon {
  &--invalid {
    color: $color-caution-accent;
  }
}
</style>
